/**
 * BXYZ:..:eliot@bosmanxyz.xyz:..:.www.bosmanxyz.xyz
 * bxyz-animations - gsap animationer och scrolltrigger
 */

/* ============================================
   konfiguration
   ============================================ */

const bxyzAnimationConfig = {
  duration: 0.8,
  ease: 'power2.out',
  stagger: 0.1,
  scrollTriggerStart: 'top 85%',
  scrollTriggerEnd: 'bottom 15%',
};

/* ============================================
   hjälpfunktioner
   ============================================ */

function bxyzQs(selector, parent = document) {
  return parent.querySelector(selector);
}

function bxyzQsa(selector, parent = document) {
  return Array.from(parent.querySelectorAll(selector));
}

function bxyzPrefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ============================================
   vänta på gsap
   ============================================ */

function bxyzWaitForGsap(callback) {
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    callback();
  } else {
    setTimeout(() => bxyzWaitForGsap(callback), 100);
  }
}

/* ============================================
   fade animationer
   ============================================ */

function bxyzInitFadeAnimations() {
  if (bxyzPrefersReducedMotion()) {
    bxyzQsa('[data-animate]').forEach((el) => {
      el.classList.add('is-visible');
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      el.style.transform = 'none';
    });
    return;
  }

  const fadeElements = bxyzQsa('[data-animate]');

  fadeElements.forEach((element) => {
    const animationType = element.getAttribute('data-animate');
    const styleAttr = element.getAttribute('style') || '';
    const delayMatch = styleAttr.match(/--animation-delay:\s*([\d.]+)s/);
    const delay = delayMatch ? parseFloat(delayMatch[1]) : 0;

    const fromVars = {
      opacity: 0,
      duration: bxyzAnimationConfig.duration,
      delay: delay,
      ease: bxyzAnimationConfig.ease,
      onComplete: () => {
        element.classList.add('is-visible');
      },
    };

    switch (animationType) {
      case 'fade-up':
        fromVars.y = 30;
        break;
      case 'fade-down':
        fromVars.y = -30;
        break;
      case 'fade-left':
        fromVars.x = 30;
        break;
      case 'fade-right':
        fromVars.x = -30;
        break;
      case 'scale':
        fromVars.scale = 0.95;
        break;
    }

    gsap.from(element, {
      ...fromVars,
      scrollTrigger: {
        trigger: element,
        start: bxyzAnimationConfig.scrollTriggerStart,
        once: true,
      },
    });
  });
}

/* ============================================
   parallax effekter
   ============================================ */

function bxyzInitParallax() {
  if (bxyzPrefersReducedMotion()) return;

  const parallaxElements = bxyzQsa('[data-parallax]');

  parallaxElements.forEach((element) => {
    const speed = parseFloat(element.getAttribute('data-parallax') || '0.5');

    gsap.to(element, {
      yPercent: speed * 20,
      ease: 'none',
      scrollTrigger: {
        trigger: element.parentElement,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });
  });
}

/* ============================================
   hero animationer
   ============================================ */

function bxyzInitHeroAnimation() {
  if (bxyzPrefersReducedMotion()) return;

  const hero = bxyzQs('.bxyz-hero');
  if (!hero) return;

  const heroContent = bxyzQs('.bxyz-hero__text', hero);
  const heroMedia = bxyzQs('.bxyz-hero__image, .bxyz-hero__video', hero);
  const heroScroll = bxyzQs('.bxyz-hero__scroll', hero);

  const tl = gsap.timeline({
    defaults: {
      duration: bxyzAnimationConfig.duration,
      ease: bxyzAnimationConfig.ease,
    },
  });

  if (heroMedia) {
    tl.from(heroMedia, {
      scale: 1.1,
      duration: 1.2,
      ease: 'power2.out',
    });
  }

  if (heroContent) {
    const children = heroContent.children;
    tl.from(
      children,
      {
        opacity: 0,
        y: 30,
        stagger: 0.15,
      },
      '-=0.6'
    );
  }

  if (heroScroll) {
    tl.from(
      heroScroll,
      {
        opacity: 0,
        y: 20,
      },
      '-=0.3'
    );
  }

  // parallax på hero bild vid scroll
  if (heroMedia) {
    gsap.to(heroMedia, {
      yPercent: 20,
      ease: 'none',
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }
}

/* ============================================
   staggered grid animationer
   ============================================ */

function bxyzInitGridAnimations() {
  if (bxyzPrefersReducedMotion()) return;

  const grids = bxyzQsa('.bxyz-featured-collection__grid, .bxyz-collection-list__grid, .bxyz-testimonials__grid');

  grids.forEach((grid) => {
    const items = grid.children;

    gsap.from(items, {
      opacity: 0,
      y: 40,
      duration: bxyzAnimationConfig.duration,
      stagger: bxyzAnimationConfig.stagger,
      ease: bxyzAnimationConfig.ease,
      scrollTrigger: {
        trigger: grid,
        start: bxyzAnimationConfig.scrollTriggerStart,
        once: true,
      },
    });
  });
}

/* ============================================
   reveal animationer för sektioner
   ============================================ */

function bxyzInitSectionReveals() {
  if (bxyzPrefersReducedMotion()) return;

  const sections = bxyzQsa('.bxyz-section');

  sections.forEach((section) => {
    const header = bxyzQs('[class*="__header"]', section);
    const content = bxyzQs('[class*="__grid"], [class*="__content"], [class*="__inner"]', section);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: bxyzAnimationConfig.scrollTriggerStart,
        once: true,
      },
    });

    if (header) {
      tl.from(header, {
        opacity: 0,
        y: 20,
        duration: bxyzAnimationConfig.duration,
        ease: bxyzAnimationConfig.ease,
      });
    }

    if (content && content !== header) {
      tl.from(
        content,
        {
          opacity: 0,
          y: 30,
          duration: bxyzAnimationConfig.duration,
          ease: bxyzAnimationConfig.ease,
        },
        header ? '-=0.4' : 0
      );
    }
  });
}

/* ============================================
   hover animationer
   ============================================ */

function bxyzInitHoverAnimations() {
  if (bxyzPrefersReducedMotion()) return;

  // produktkort hover
  const productCards = bxyzQsa('.bxyz-product-card');

  productCards.forEach((card) => {
    const image = bxyzQs('.bxyz-product-card__image', card);
    const quickAdd = bxyzQs('.bxyz-product-card__quick-add', card);

    if (image) {
      card.addEventListener('mouseenter', () => {
        gsap.to(image, {
          scale: 1.05,
          duration: 0.4,
          ease: 'power2.out',
        });
      });

      card.addEventListener('mouseleave', () => {
        gsap.to(image, {
          scale: 1,
          duration: 0.4,
          ease: 'power2.out',
        });
      });
    }

    if (quickAdd) {
      card.addEventListener('mouseenter', () => {
        gsap.to(quickAdd, {
          opacity: 1,
          y: 0,
          duration: 0.3,
          ease: 'power2.out',
        });
      });

      card.addEventListener('mouseleave', () => {
        gsap.to(quickAdd, {
          opacity: 0,
          y: 10,
          duration: 0.3,
          ease: 'power2.out',
        });
      });
    }
  });
}

/* ============================================
   knapp animationer
   ============================================ */

function bxyzInitButtonAnimations() {
  if (bxyzPrefersReducedMotion()) return;

  const buttons = bxyzQsa('.bxyz-btn');

  buttons.forEach((button) => {
    button.addEventListener('mouseenter', () => {
      gsap.to(button, {
        scale: 1.02,
        duration: 0.2,
        ease: 'power2.out',
      });
    });

    button.addEventListener('mouseleave', () => {
      gsap.to(button, {
        scale: 1,
        duration: 0.2,
        ease: 'power2.out',
      });
    });

    button.addEventListener('mousedown', () => {
      gsap.to(button, {
        scale: 0.98,
        duration: 0.1,
        ease: 'power2.out',
      });
    });

    button.addEventListener('mouseup', () => {
      gsap.to(button, {
        scale: 1.02,
        duration: 0.1,
        ease: 'power2.out',
      });
    });
  });
}

/* ============================================
   scroll progress
   ============================================ */

function bxyzInitScrollProgress() {
  const progressBar = bxyzQs('[data-scroll-progress]');
  if (!progressBar) return;

  gsap.to(progressBar, {
    scaleX: 1,
    ease: 'none',
    scrollTrigger: {
      trigger: document.body,
      start: 'top top',
      end: 'bottom bottom',
      scrub: true,
    },
  });
}

/* ============================================
   refresh scrolltrigger
   ============================================ */

function bxyzRefreshScrollTrigger() {
  if (typeof ScrollTrigger !== 'undefined') {
    ScrollTrigger.refresh();
  }
}

/* ============================================
   cleanup
   ============================================ */

function bxyzKillAllAnimations() {
  if (typeof ScrollTrigger !== 'undefined') {
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  }
  if (typeof gsap !== 'undefined') {
    gsap.killTweensOf('*');
  }
}

/* ============================================
   initiering
   ============================================ */

function bxyzInitAllAnimations() {
  bxyzInitFadeAnimations();
  bxyzInitParallax();
  bxyzInitHeroAnimation();
  bxyzInitGridAnimations();
  bxyzInitSectionReveals();
  bxyzInitHoverAnimations();
  bxyzInitButtonAnimations();
  bxyzInitScrollProgress();
}

// vänta på dom och gsap
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bxyzWaitForGsap(bxyzInitAllAnimations);
  });
} else {
  bxyzWaitForGsap(bxyzInitAllAnimations);
}

// re-init vid shopify section rendering
document.addEventListener('shopify:section:load', () => {
  bxyzRefreshScrollTrigger();
  bxyzInitAllAnimations();
});

// exportera för extern användning
window.bxyzAnimations = {
  init: bxyzInitAllAnimations,
  refresh: bxyzRefreshScrollTrigger,
  kill: bxyzKillAllAnimations,
  initFade: bxyzInitFadeAnimations,
  initHero: bxyzInitHeroAnimation,
  initGrid: bxyzInitGridAnimations,
  initHover: bxyzInitHoverAnimations,
};
